import { useRef, useState } from 'react'
import { Check } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { showError } from '@/lib/toast'
import {
  useCorrectDealLead,
  useDealLeadFieldOptions,
} from '@/hooks/use-deal-leads'
import {
  buildCorrection,
  isNumericKind,
  toInputValue,
  unitFor,
} from '@/features/deal-review/deal-lead-corrections'
import {
  optionLabel,
  picklistFor,
  unmatchedFor,
  unmatchedLabel,
} from '@/features/deal-review/field-picklist'
import type { CorrectableField } from '@/features/deal-review/deal-lead-corrections'
import type { DealLeadDetail, FieldOption } from '@/types/api'

// No version conflicts: saves write fresh detail to the cache and the editor won't open mid-save.
export function InlineFieldValue({
  lead,
  field,
  display,
  className,
}: {
  lead: DealLeadDetail
  field: CorrectableField
  display: React.ReactNode
  className?: string
}) {
  const save = useCorrectDealLead(lead.id)
  const fieldOptions = useDealLeadFieldOptions()
  const [editing, setEditing] = useState(false)
  // Escape unmounts the editor, firing blur; without this the cancel would commit.
  const cancelled = useRef(false)

  const picklist =
    field.kind === 'text' ? picklistFor(fieldOptions.data, field.key) : null
  const stored = toInputValue(lead, field)
  const unmatched = unmatchedFor(lead, field.key)
  const shown = (
    <>
      {(picklist && optionLabel(picklist, stored)) ?? display}
      {unmatched && (
        <Typography
          as="span"
          variant="body-sm"
          className="ml-1.5 text-amber-900"
        >
          · {unmatchedLabel(unmatched.reason)}
        </Typography>
      )}
    </>
  )

  if (!lead.capabilities.canCorrect) {
    return <span className={className}>{shown}</span>
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={save.isPending}
        className={cn(
          'max-w-full justify-self-start border-b border-dashed border-primary/55 text-left',
          save.isPending && 'opacity-60',
          className
        )}
        onClick={() => {
          cancelled.current = false
          setEditing(true)
        }}
      >
        {save.isPending ? 'Saving…' : shown}
      </button>
    )
  }

  const commit = (raw: string) => {
    if (cancelled.current) return
    setEditing(false)

    const built = buildCorrection(lead, { [field.key]: raw })
    if (!built.ok) {
      showError(new Error(built.messages.join('. ')), 'That is not a number')
      return
    }
    // An unchanged save would be logged on the activity entry as a rewrite.
    if (built.changedKeys.length === 0) return

    save.mutate(
      { version: lead.version, ...built.changes },
      {
        onError: (error) =>
          showError(error, 'The correction could not be saved'),
      }
    )
  }

  if (picklist) {
    return (
      // Modal so the list scrolls: the sheet's scroll lock swallows wheel events on portals.
      <Popover modal open onOpenChange={(open) => !open && setEditing(false)}>
        <PopoverAnchor asChild>
          <span className={cn('justify-self-start', className)}>{shown}</span>
        </PopoverAnchor>
        <PopoverContent align="start" className="w-72 p-0">
          <FieldPicker
            label={field.label}
            options={picklist}
            selected={stored}
            onPick={commit}
          />
        </PopoverContent>
      </Popover>
    )
  }

  const shared = {
    autoFocus: true,
    defaultValue: toInputValue(lead, field),
    'aria-label': `${field.label}, correcting`,
    onBlur: (event: { target: { value: string } }) =>
      commit(event.target.value),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelled.current = true
        setEditing(false)
      }
    },
  }

  if (field.kind === 'longText') {
    return (
      <Textarea
        {...shared}
        rows={3}
        className="min-h-0 w-full rounded-lg border-primary/55 px-2 py-1.5 text-sm"
      />
    )
  }

  const unit = unitFor(field.kind)

  return (
    <span className="flex items-center gap-1.5">
      <Input
        {...shared}
        inputMode={isNumericKind(field.kind) ? 'decimal' : undefined}
        className="h-7 w-full rounded-lg border-primary/55 px-2 text-sm"
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit(event.currentTarget.value)
          }
          shared.onKeyDown(event)
        }}
      />
      {unit && (
        <Typography
          as="span"
          variant="body-sm"
          className="shrink-0 text-primary/70"
        >
          {unit}
        </Typography>
      )}
    </span>
  )
}

function FieldPicker({
  label,
  options,
  selected,
  onPick,
}: {
  label: string
  options: FieldOption[]
  selected: string
  onPick: (value: string) => void
}) {
  return (
    <Command>
      <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
      <CommandList>
        <CommandEmpty>No matching value in DealCloud.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.value}
              value={option.label}
              onSelect={() => onPick(option.value)}
            >
              <Check
                aria-hidden="true"
                className={cn(
                  'size-3.5',
                  option.value === selected ? 'opacity-100' : 'opacity-0'
                )}
              />
              <span className="truncate">{option.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {selected && (
          <CommandGroup className="border-t border-primary/5">
            <CommandItem value="__clear" onSelect={() => onPick('')}>
              <span className="text-primary/70">
                Clear {label.toLowerCase()}
              </span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  )
}
